import React, { createContext, useContext, useState, ReactNode } from 'react';

export type DialogType =
  | 'fileOptions'
  | 'addPermission'
  | 'createUserGroup'
  | 'emailUsers'
  | 'fileLog'
  | 'managePermissions'
  | 'selectEmailGroups'
  | 'userPermissionsList';

export type DialogKey = `${DialogType}:${string}`; // e.g. "fileOptions:/project/docs"

type DialogStateContextType = {
  isOpen: (key: DialogKey) => boolean;
  open: (key: DialogKey) => void;
  close: (key: DialogKey) => void;
  openDialogs: Set<DialogKey>;
};

const DialogStateContext = createContext<DialogStateContextType | undefined>(undefined);

export const DialogStateProvider = ({ children }: { children: ReactNode }) => {
  const [openDialogs, setOpenDialogs] = useState<Set<DialogKey>>(new Set());

  const isOpen = (key: DialogKey) => openDialogs.has(key);

  const open = (key: DialogKey) => {
    setOpenDialogs((prev) => new Set(prev).add(key));
  };

  const close = (key: DialogKey) => {
    setOpenDialogs((prev) => {
      const copy = new Set(prev);
      copy.delete(key);
      return copy;
    });
  };

  return (
    <DialogStateContext.Provider value={{ isOpen, open, close, openDialogs  }}>
      {children}
    </DialogStateContext.Provider>
  );
};

export const useDialogState = () => {
  const ctx = useContext(DialogStateContext);
  if (!ctx) throw new Error('useDialogState must be used within a DialogStateProvider');
  return ctx;
};
